import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import lodash from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  ExpandFilters,
  FilterHeadWrapper,
  FilterMenu,
  FiltersList,
  FiltersWrapper,
  HideFilters,
  Title,
  TitleAndClose,
} from './Filters.style';
import { addOrRemoveFilter, changeRangeFilter, selectFilters } from '../../redux/filtersReducer';
import BackIcon from '../../assets/icons/BackIcon';
import SortByMobile from './SortBy';
import LocationFilter from './LocationFilter/LocationFilter';
import { selectLocation } from '../../redux/locationReducer';
import { selectAppConfig } from '../../redux/appConfigReducer';
import { LocationSearchData } from '../../types';
import CloseIcon from '../../assets/icons/CloseIcon';
import {
  ALL_FILTERS_CLOSED,
  ALL_FILTERS_OPENED,
  DEFAULT_FILTERS_OPENED,
} from '../../constants/initialFilters';
import { filtersChange } from '../../utils/analyticsEventListeners';
import CtaGroup from './CtaGroup';
import useScrollTracker from '../../hooks/UI/useScrollTracker';
import useFilterListBuilder from '../../hooks/useFilterListBuilder';
import useFilterOptions from '../../hooks/Filters/useFilterOptions';

interface FiltersTypes {
  isLaptop: boolean;
  isFiltersOpen: boolean;
  isReachedEndOfContainer: boolean;
  isVehiclesLoading: boolean;
  setIsFiltersOpen: (arg: boolean) => void;
}

const Filters: React.FC<FiltersTypes> = ({
  isLaptop,
  isFiltersOpen,
  isReachedEndOfContainer,
  isVehiclesLoading,
  setIsFiltersOpen,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // flag to track temp filter changes only when users change somthing - for data layer events
  const [filtersChangedFlag, setFiltersChangedFlag] = useState<boolean>(false);
  const [openedFiltersList, setOpenedFiltersList] = useState(DEFAULT_FILTERS_OPENED);

  const { location } = useSelector(selectLocation);
  const { marketType, isTier3page, isUrlParamsDynamic } = useSelector(selectAppConfig);
  const { confirmedFilters } = useSelector(selectFilters);

  const { sortMethods } = useFilterOptions();

  const filtersRef = useRef(confirmedFilters);

  const handleAddFilter = (filterType: string, filterValue: string) => {
    setFiltersChangedFlag(true);
    dispatch(
      addOrRemoveFilter({
        filterType,
        filterValue,
        isUrlParamsDynamic: isUrlParamsDynamic ? 'true' : 'false',
        isTier3page: isTier3page ? 'true' : 'false',
      }),
    );
  };

  const handleChangeRange = (filterType: string, filterValue: number[]) => {
    setFiltersChangedFlag(true);
    dispatch(
      changeRangeFilter({
        [filterType]: filterValue,
        isUrlParamsDynamic: isUrlParamsDynamic ? 'true' : 'false',
        isTier3page: isTier3page ? 'true' : 'false',
      }),
    );
    // Flag responsible for handling data layer events => Filter Panel Change
  };

  const handleExposeFilter = (filterType: string) => {
    setOpenedFiltersList((prevState) => ({ ...prevState, [filterType]: !prevState[filterType] }));
  };

  const allFiltersOpened = Object.values(openedFiltersList).some((filter) => filter);
  const allFiltersClosed = Object.values(openedFiltersList).every((filter) => !filter);

  const handleShowOrHideAllFilters = () => {
    if (allFiltersOpened) {
      setOpenedFiltersList(ALL_FILTERS_CLOSED);
    }
    if (allFiltersClosed) {
      setOpenedFiltersList(ALL_FILTERS_OPENED);
    }
  };

  useEffect(() => {
    // keep track of changes on temp filters
    // so we could call Filter Panel Change data layer event
    if (lodash.isEqual(confirmedFilters, filtersRef.current)) return;
    if (filtersChangedFlag) {
      filtersChange(filtersRef.current, confirmedFilters);
      setFiltersChangedFlag(false);
    }
    filtersRef.current = confirmedFilters;
  }, [filtersChangedFlag, confirmedFilters]);

  const isPassedNav = useScrollTracker(72);

  const listOfFilters = useFilterListBuilder(
    handleAddFilter,
    openedFiltersList,
    handleExposeFilter,
    handleChangeRange,
    isVehiclesLoading,
  );

  const renderSortBy = useMemo(() => {
    if (!isLaptop)
      return (
        <SortByMobile
          sortMethods={sortMethods}
          openedFiltersList={openedFiltersList}
          handleExposeFilter={handleExposeFilter}
          filterType="sortBy"
        />
      );
    return null;
  }, [isLaptop, openedFiltersList, sortMethods]);

  return (
    <FilterMenu>
      <FiltersWrapper data-testid="filters-wrapper">
        <FilterHeadWrapper>
          <TitleAndClose>
            <Title>
              {isLaptop ? t('vlp.filters.inventoryFilters') : t('vlp.filters.sortAndFilter')}
            </Title>
            <HideFilters
              onClick={() => {
                setIsFiltersOpen(false);
              }}
            >
              {isLaptop ? (
                <>
                  <BackIcon />
                  {t('vlp.filters.hideFilters')}
                </>
              ) : (
                <CloseIcon />
              )}
            </HideFilters>
          </TitleAndClose>

          {!isLaptop && (
            <ExpandFilters onClick={handleShowOrHideAllFilters}>
              {allFiltersClosed ? t('vlp.filters.expandFilters') : t('vlp.filters.collapseFilters')}
            </ExpandFilters>
          )}
        </FilterHeadWrapper>
        <FiltersList isReachedEndOfContainer={isReachedEndOfContainer} isPassedNav={isPassedNav}>
          {/* Sort by Selector. Here only on mobile */}
          {renderSortBy}
          {/* Location selector, here only on desktop */}
          {isLaptop && !isTier3page && (
            <LocationFilter
              userSelectedLocation={location as LocationSearchData}
              market={marketType}
              isLaptop={isLaptop}
            />
          )}
          {listOfFilters.map((vehicleFilter) => {
            return <div key={vehicleFilter.value}>{vehicleFilter.Component}</div>;
          })}
        </FiltersList>
        {!isLaptop && (
          <CtaGroup
            isFiltersOpen={isFiltersOpen}
            isVehiclesLoading={isVehiclesLoading}
            loading={isVehiclesLoading}
            isLaptop={isLaptop}
            isReachedEndOfContainer={isReachedEndOfContainer}
            setIsFiltersOpen={setIsFiltersOpen}
          />
        )}
      </FiltersWrapper>
    </FilterMenu>
  );
};

export default Filters;
